import { useState, useEffect, useCallback, useRef } from 'react';
import { useUserInfo } from '@/app/stores/useUserStore';
import { REWARD_MODAL_CACHE_KEY, REWARD_MODAL_CACHE_DURATION } from '@/app/const/locale';
import { fetchCurrentWeek } from '@/lib/creator-api';

export interface RewardPlanData {
  /** 奖励金额 */
  amount: number;
  /** 活动结束时间（时间戳 ms） */
  endTime: number;
  /** 是否进行中 */
  active: boolean;
}

export const useRewardModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isManualOpen, setIsManualOpen] = useState(false);
  /** 是否应该自动弹出（用于与邀请弹窗互斥判断） */
  const [shouldAutoShow, setShouldAutoShow] = useState(false);
  /** 活动数据是否正在加载（加载期间邀请弹窗应等待） */
  const [loading, setLoading] = useState(true);
  const [rewardData, setRewardData] = useState<RewardPlanData | null>(null);
  const userInfo = useUserInfo();
  const userIdRef = useRef(userInfo?.userId);
  userIdRef.current = userInfo?.userId;

  // 检查缓存是否过期
  const shouldShowModal = useCallback(() => {
    const closedTime = localStorage.getItem(REWARD_MODAL_CACHE_KEY + userIdRef.current);
    if (!closedTime) return true;
    const timeDiff = Date.now() - parseInt(closedTime, 10);
    return timeDiff >= REWARD_MODAL_CACHE_DURATION;
  }, []);

  // 关闭弹窗并记录时间
  const closeModal = useCallback(() => {
    setIsOpen(false);
    setIsManualOpen(false);
    localStorage.setItem(REWARD_MODAL_CACHE_KEY + userIdRef.current, Date.now().toString());
  }, []);

  // 手动打开弹窗
  const openModal = useCallback(() => {
    setIsManualOpen(true);
    setIsOpen(true);
  }, []);

  // 页面加载时请求活动数据，判断是否自动弹出
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    fetchCurrentWeek().then((weekInfo) => {
      if (!weekInfo) {
        setLoading(false);
        return;
      }
      setRewardData({
        amount: weekInfo.prizePool,
        endTime: weekInfo.endTimestamp,
        active: weekInfo.active,
      });
      if (weekInfo.active && shouldShowModal()) {
        setShouldAutoShow(true);
        timer = setTimeout(() => {
          setIsOpen(true);
        }, 1000);
      }
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });

    return () => clearTimeout(timer);
  }, [userInfo, shouldShowModal]);

  // 监听手动打开
  useEffect(() => {
    if (isManualOpen) {
      setIsOpen(true);
    }
  }, [isManualOpen]);

  return {
    isOpen,
    openModal,
    closeModal,
    isManualOpen,
    /** 是否满足自动弹出条件（用于互斥邀请弹窗） */
    shouldAutoShow,
    /** 活动数据是否正在加载 */
    loading,
    /** 活动数据 */
    rewardData,
  };
};
